/**
 * The match API contract (docs/14-SLIME-WARS/TECH.md §6): intents in, events and a read-only view
 * out. The server room, the browser's offline Practice and the bots all speak exactly this.
 * Coordinates are world px, +y down; angles are radians; time is ticks (30 Hz).
 */
import type {
  BodyKind,
  BotDifficulty,
  BuffId,
  ClassId,
  CrateKind,
  CrateTier,
  DeathCause,
  DrillRevertReason,
  EffectEndReason,
  EffectId,
  PickupItemId,
  SlimeForm,
  SlimeMotionState,
  TauntId,
  TurnPhase,
  WeaponId,
} from '../content/ids';
import type { MapStyle, MapTheme, WarsSettings } from '../content/settings';
import type { SeatTeam, TeamId } from '../content/teams';
import type { RngSecret } from '../rng';
import type { KeepCircle } from '../terrain/carve';
import type { TerrainMask } from '../terrain/types';

export const WARS_TICK_RATE_HZ = 30;
export const WARS_TICK_DT = 1 / WARS_TICK_RATE_HZ;

/** Facing: +1 right, −1 left. */
export type Facing = 1 | -1;

/** Keys a player holds (movement / jetpack thrust). Sent on change; the latest one applies. */
export interface HeldKeys {
  readonly left: boolean;
  readonly right: boolean;
  /** Jetpack upward thrust (ignored when not jetpacking). */
  readonly up: boolean;
}

export const NO_HELD_KEYS: HeldKeys = Object.freeze({ left: false, right: false, up: false });

/**
 * One-shot player decisions (RULES §4). `elevation` ∈ [−π/2, π/2] relative to `facing`
 * (+ = up); `power` ∈ [0, 1]. Out-of-range numbers are clamped, non-finite ones drop the command.
 */
export type WarsCommand =
  | { readonly type: 'aim'; readonly elevation: number; readonly facing: Facing }
  /** Started charging (public cue only; power is chosen by the client and sent with `fire`). */
  | { readonly type: 'charge' }
  /** Stopped charging without firing (right-click / Esc / blur; public cue only, ADR-039). */
  | { readonly type: 'chargeCancel' }
  | {
      readonly type: 'fire';
      readonly elevation: number;
      readonly facing: Facing;
      readonly power: number;
    }
  /**
   * The aimed jump, the slime's only jump (RULES §2, ADR-039; W026a removed `hop` / `bounce`):
   * along `elevation` (clamped to the jump's range) on the `facing` side; `power` ∈ [0, 1] chosen
   * by the client (hold time, `jumpPowerForHold`). In jetpack flight it ends the flight.
   */
  | {
      readonly type: 'jump';
      readonly elevation: number;
      readonly facing: Facing;
      readonly power: number;
    }
  | { readonly type: 'select'; readonly weapon: WeaponId }
  /**
   * Mark another living slime with the own equipped taunt (W030, ADR-043): own AIMING only, once
   * per turn, before the turn-ending shot. Cosmetic: settled at this turn's end (`tauntResult`).
   */
  | { readonly type: 'taunt'; readonly target: number }
  /** Target point for targeted weapons (seeker, meteors, teleport). */
  | { readonly type: 'target'; readonly x: number; readonly y: number }
  | { readonly type: 'jetpackOff' }
  /** End the turn now (skip go). */
  | { readonly type: 'skip' };

export type WarsCommandType = WarsCommand['type'];

export const WARS_COMMAND_TYPES = [
  'aim',
  'charge',
  'chargeCancel',
  'fire',
  'jump',
  'select',
  'taunt',
  'target',
  'jetpackOff',
  'skip',
] as const satisfies readonly WarsCommandType[];

export interface WarsPlayerConfig {
  /** Seat slot, 0 … 7 (colour index, turn order). */
  readonly slot: number;
  readonly classId: ClassId;
  readonly name: string;
  readonly bot: BotDifficulty | null;
  /** The equipped taunt (W030, ADR-043); absent: `DEFAULT_TAUNT_ID`. */
  readonly taunt?: TauntId;
  /**
   * The seat's team (W044, ADR-051; TEAMS.md §3): A–D, absent = Solo. A match is a team match only
   * when some team has two or more players; otherwise every `team` is ignored and the match is
   * today's free-for-all (TEAMS.md §3.3). Any other value is a `RangeError`.
   */
  readonly team?: TeamId;
}

export interface WarsMatchConfig {
  /**
   * 32-bit unsigned PUBLIC map seed: map generation (terrain, props, a 'random' theme / style),
   * spawn points and who gets which, mine and barrel positions. Everything it drives is public
   * anyway (the terrain is sent), so knowing it reveals nothing hidden (TECH §2).
   */
  readonly seed: number;
  /**
   * 128-bit match secret (4 unsigned 32-bit words) seeding every hidden or unpredictable stream:
   * wind, crate drops (chance, kind, x), crate contents, dud mines, shot spread / flames /
   * bomblets, the first player, and the bots (TECH §2, §8). The server and local Practice fill it
   * with crypto randomness; tests and replays pass explicit values. Omitted → derived from
   * `seed` (`secretFromSeed`): tests and harnesses only, nothing is hidden then.
   */
  readonly secret?: RngSecret;
  readonly settings: WarsSettings;
  /** 2 … 8 players, distinct slots. */
  readonly players: readonly WarsPlayerConfig[];
}

/** Every event carries the tick it happened on (the client plays it when its render time gets there). */
interface EventBase {
  readonly tick: number;
}

/**
 * Integer terrain change (TECH §4.3; the terrain's `TerrainCarve`); `version` is the terrain
 * version after it. Six shapes, by their fields (`carveKind`; anything else is malformed,
 * `isTerrainCarve`):
 *
 * - a crater: `{ cx, cy, r, version }` — every cell but BEDROCK within r goes;
 * - W053 (ADR-055) the melee smash: `+ x2, y2, wood: true` — the WOOD and (W054) MUD cells within
 *   r of the segment (cx, cy)–(x2, y2) (on a swing's `explosion` event: the client's chip cue);
 * - W054 (ADR-056) the Moleman's tunnel: `+ x2, y2` — every cell but BEDROCK within r of the
 *   segment (on the drill's `explosion` events, `source: 'ability.drill'`);
 * - W056 (ADR-057) the Spade: `+ x2, y2, mud: true` — the MUD cells within r of the segment (on
 *   the dig's `explosion` event, `source: 'ability.spade'`);
 * - W054 the mud mound: `+ fill: 'mud', keep, maxY` — the AIR cells of the disk become MUD,
 *   except inside a `keep` circle `[x, y, r]`, below row `maxY` or outside the map (on a Mud
 *   blast's `explosion` event, `source: 'basic.mud'`);
 * - W057 (ADR-058) the Earthquake's shatter: `+ flood: 'wood'` — the WOOD cells 8-connected to the
 *   seed (cx, cy) within the square |x − cx| ≤ r, |y − cy| ≤ r (`source: 'shatter'`; the
 *   quake's fissures are tunnel bands, `source: 'fissure'`).
 */
export interface CarveSpec {
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
  readonly version: number;
  /** A band's other end (W053 the smash, W054 the tunnel); with `y2`, absent on a disk / fill. */
  readonly x2?: number;
  readonly y2?: number;
  /** W053: the smash band (WOOD and, since W054, MUD go); absent on every other carve. */
  readonly wood?: true;
  /** W056: the Spade's band (only MUD goes); absent on every other carve. */
  readonly mud?: true;
  /** W054: a mound — the disk's AIR cells become MUD. */
  readonly fill?: 'mud';
  /** W054: the mound's keep-out circles, integers `[x, y, r]` (with `fill` only). */
  readonly keep?: readonly KeepCircle[];
  /** W054: the lowest row the mound may fill, inclusive (with `fill` only; the water margin). */
  readonly maxY?: number;
  /** W057: a shattered prop — the WOOD flood from the seed (cx, cy) within reach r. */
  readonly flood?: 'wood';
}

/**
 * A crate's tier as events and views carry it (W043a, LOOT.md §11.1): `rare` / `epic`, ABSENT for
 * Common — so a Classic match's events and views are byte for byte today's.
 */
export type UncommonTier = Exclude<CrateTier, 'common'>;

/**
 * What exploded (presentation: colour, debris, sound): a body kind, a weapon id, a popping slime
 * (`death`), a Wrath of God strike (`wrath`, ADR-046) or an Earthquake's carve (W043a, W057,
 * LOOT.md §3.4): a crumble (`quake`), a fissure's band (`fissure`) or a shattered prop
 * (`shatter`).
 */
export type ExplosionSource =
  BodyKind | WeaponId | 'death' | 'wrath' | 'quake' | 'fissure' | 'shatter';

/**
 * How a slime lost HP (`damage.kind`). `suddenDeath`: the `oneHp` sudden death dropped the slime to
 * 1 HP (ADR-041). `wrath`: a Wrath of God strike's blast (ADR-046; `by` null). `quake`: the
 * Earthquake (W043a, LOOT.md §3.2; by the shooter, armor absorbs, the guard floors it).
 */
export type WarsDamageKind =
  'blast' | 'hit' | 'fall' | 'fire' | 'poison' | 'suddenDeath' | 'wrath' | 'quake';

export type WarsEvent =
  | (EventBase & {
      readonly type: 'turnStart';
      readonly slot: number;
      readonly turn: number;
      readonly wind: number;
    })
  | (EventBase & {
      /**
       * This turn of `slot` is skipped (W021a, RULES §3): a Confused slime lost its skip roll at
       * its TURN_INTRO. Emitted in the tick of that `turnStart`, after it; when the intro ends the
       * turn goes on to SETTLING with no AIMING (no move, no shot, no RETREAT).
       */
      readonly type: 'turnSkipped';
      readonly slot: number;
      readonly reason: 'confused';
    })
  | (EventBase & {
      readonly type: 'fire';
      readonly slot: number;
      readonly weapon: WeaponId;
      /**
       * W053 (ADR-055): a clearing swing — a Bat swing whose reach met no other living slime,
       * paid with movement points: the turn goes on (AIMING), it is no shot (not in the stats,
       * Double Damage stays). W056 (ADR-057): every Spade scoop (`weapon: 'ability.spade'`) is one
       * too — its dig, when it took mud, is the `explosion` that follows in the same tick. Absent
       * on every other `fire`.
       */
      readonly clear?: true;
    })
  /** `slot` marked `target` with its equipped `taunt` (W030, ADR-043). */
  | (EventBase & {
      readonly type: 'tauntSet';
      readonly slot: number;
      readonly target: number;
      readonly taunt: TauntId;
    })
  /**
   * The taunt of `slot` on `target` settled at the end of the taunter's turn (W030): `success` when
   * `target` was eliminated that turn with the kill credited to `slot` (the `kills` credit, RULES
   * §10; W030a, RULES §15).
   */
  | (EventBase & {
      readonly type: 'tauntResult';
      readonly slot: number;
      readonly target: number;
      readonly taunt: TauntId;
      readonly success: boolean;
    })
  | (EventBase & {
      readonly type: 'tracer';
      readonly slot: number;
      readonly fromX: number;
      readonly fromY: number;
      readonly toX: number;
      readonly toY: number;
    })
  | (EventBase & {
      readonly type: 'explosion';
      readonly x: number;
      readonly y: number;
      readonly r: number;
      /** What exploded (presentation: colour, debris, sound). */
      readonly source: ExplosionSource;
      /** Absent when nothing was carved (r 0 / melee). */
      readonly carve?: CarveSpec;
      /**
       * W054b-R: the driller's slot on a tunnel band (`source: 'ability.drill'`); W056: the
       * digger's on a Spade's dig (`source: 'ability.spade'`); absent on every other explosion.
       */
      readonly slot?: number;
      /**
       * W057: which of the quake's fissures this band belongs to (`source: 'fissure'`): 0, 1, 2 …
       * in the order they open; its bands follow in order, top down. Absent on every other
       * explosion (presentation: the client unzips each fissure along its bands).
       */
      readonly fissure?: number;
    })
  | (EventBase & {
      readonly type: 'damage';
      readonly slot: number;
      readonly amount: number;
      readonly hp: number;
      /** Slot of the player whose action caused it; null for the environment. */
      readonly by: number | null;
      /** `WarsDamageKind`. */
      readonly kind: WarsDamageKind;
    })
  | (EventBase & {
      readonly type: 'heal';
      readonly slot: number;
      readonly amount: number;
      readonly hp: number;
    })
  | (EventBase & { readonly type: 'poisoned'; readonly slot: number; readonly turns: number })
  | (EventBase & {
      readonly type: 'armor';
      readonly slot: number;
      readonly absorbed: number;
      readonly left: number;
    })
  | (EventBase & {
      readonly type: 'pickup';
      readonly slot: number;
      readonly crate: CrateKind;
      readonly item: PickupItemId;
      /** W043 (LOOT.md §2.3): the crate's tier; absent: Common. */
      readonly tier?: UncommonTier;
      /**
       * W043: the charges granted, only when not the item's standard ammo (a Rare bundle: 2 ×).
       * Absent: `WEAPONS[item].ammo` (or not a weapon).
       */
      readonly ammo?: number;
      /** W043: a Rare utility pack's second utility (applied after `item`); absent otherwise. */
      readonly also?: BuffId;
    })
  | (EventBase & {
      /**
       * A crate appears (RULES §7): a parachute drop at TURN_INTRO enters above the map top at `x`.
       * W043 (LOOT.md §6.4, §7): a cave drop or a crystal's crate (`cave: true`) materialises at
       * rest at (`x`, `y`) — `y` its resting centre, present exactly when `cave` is.
       */
      readonly type: 'crateDrop';
      readonly id: number;
      readonly crate: CrateKind;
      readonly x: number;
      /** W043: the crate's tier; absent: Common. */
      readonly tier?: UncommonTier;
      /** W043: it materialised in a cave (no parachute). */
      readonly cave?: true;
      /** W043: a cave crate's resting centre y. */
      readonly y?: number;
    })
  | (EventBase & {
      readonly type: 'crateBreak';
      readonly id: number;
      readonly x: number;
      readonly y: number;
    })
  | (EventBase & { readonly type: 'mineBeep'; readonly id: number })
  | (EventBase & {
      /**
       * W043 (LOOT.md §7): a breakable object broke (step 4) — a gas canister (it rockets: the
       * `canister` body takes its place) or a crystal cluster (it shatters: its shard blast
       * follows). Silent in text; its effect speaks.
       */
      readonly type: 'objectBreak';
      readonly id: number;
      readonly kind: 'gas_can' | 'crystal';
      readonly x: number;
      readonly y: number;
    })
  | (EventBase & {
      /**
       * W043 (LOOT.md §3.4): `slot`'s Orbital Laser fires down column `x` from `top` to `bottom`
       * (step 4), before the shaft's carves (`explosion` events of r 0) and its hits.
       */
      readonly type: 'beam';
      readonly slot: number;
      readonly x: number;
      readonly top: number;
      readonly bottom: number;
    })
  | (EventBase & {
      /**
       * W043 (LOOT.md §3.4): `slot`'s Earthquake strikes from (`x`, `y`) (step 4), before its
       * damage, jostles and crumble carves (`explosion` events of r 0, source `quake`).
       */
      readonly type: 'quake';
      readonly slot: number;
      readonly x: number;
      readonly y: number;
    })
  | (EventBase & {
      readonly type: 'teleport';
      readonly slot: number;
      readonly fromX: number;
      readonly fromY: number;
      readonly toX: number;
      readonly toY: number;
    })
  /*
   * The Moleman's Drill (W054, ADR-056, MOLEMAN.md §3, §5): the moments the client animates. The
   * view's `SlimeView.form` holds the lasting state; the tunnel's carves are `explosion` events of
   * r 0 with `source: 'ability.drill'` and a drill band `carve { cx, cy, r, x2, y2, version }`;
   * a ram's damage is the usual `damage` (kind `hit`, by the driller).
   */
  | (EventBase & {
      /**
       * `slot`'s slime starts turning into a drill at (x, y): its `fire` with `ability.drill`
       * (step 1, in its AIMING, on the ground; the turn's attack is used up — `canFire` false).
       * `form` is `'drill'` from this tick. It holds still until `readyTick` (= tick +
       * `DRILL.transformSeconds`), when the transform is done and the drill may drive.
       */
      readonly type: 'drillStart';
      readonly slot: number;
      readonly x: number;
      readonly y: number;
      readonly readyTick: number;
    })
  | (EventBase & {
      /**
       * `slot`'s driving drill rammed `target` at (x, y) — the point between them where the
       * circles met — along (dirX, dirY), the drill's unit direction. Once per turn per target;
       * the drill stops there. The target's `damage` (kind `hit`, by `slot`) and its knock follow
       * in the same tick, when friendly fire and the first-turn guard let them through.
       */
      readonly type: 'drillRam';
      readonly slot: number;
      readonly target: number;
      readonly x: number;
      readonly y: number;
      readonly dirX: number;
      readonly dirY: number;
    })
  | (EventBase & {
      /**
       * `slot`'s drill starts turning back into a slime at (x, y), for `reason` (a client reads
       * one it does not know as `stop`). The turn goes to SETTLING in this tick (no RETREAT). It
       * holds still until `endTick` (= tick + `DRILL.revertSeconds`): `form` stays `'drill'`
       * until then and is `'slime'` (absent) from `endTick` on, when the slime pops out and
       * falls as any slime.
       */
      readonly type: 'drillRevert';
      readonly slot: number;
      readonly x: number;
      readonly y: number;
      readonly reason: DrillRevertReason;
      readonly endTick: number;
    })
  | (EventBase & { readonly type: 'splash'; readonly x: number; readonly size: 'small' | 'big' })
  | (EventBase & { readonly type: 'land'; readonly slot: number; readonly speed: number })
  | (EventBase & {
      readonly type: 'death';
      readonly slot: number;
      readonly cause: DeathCause;
      readonly by: number | null;
      /**
       * Set (true) when a `water` death happened beyond a side edge of the map (x < 0 or
       * x > width − 1): the slime flew off into the distance rather than sinking in the map's
       * own water (W011a, presentation only; absent otherwise).
       */
      readonly oob?: true;
    })
  | (EventBase & {
      readonly type: 'waterWarning';
      /**
       * Stalemate water is near (W018, W17R-m3): the calm-turn count reached 19 at this
       * TURN_END. If nobody loses HP in the turn it opens, the water rises at the end of the turn
       * after (the `suddenDeath` event with `reason: 'stalemate'`). Never sent while the water
       * already rises.
       */
      readonly reason: 'stalemate';
    })
  | (EventBase & {
      readonly type: 'suddenDeath';
      /**
       * Absent: the sudden-death timer ran out. `'stalemate'`: the water started rising because
       * nobody lost HP for 20 turns in a row (W016a, stalemate water; same rise rule).
       */
      readonly reason?: 'stalemate';
      /**
       * `'oneHp'` (ADR-041, `suddenDeathEffect`): the timer's sudden death also drops every living
       * slime to 1 HP — one `damage` event of kind `suddenDeath` each, right after this one. Absent:
       * the water only. Never on a stalemate.
       */
      readonly effect?: 'oneHp';
    })
  /*
   * The Wrath of God (ADR-046, RULES §9.1, TECH §6.1). The view's `wrath` holds the lasting state
   * (a (re)join reads it there); these events are the moments.
   */
  | (EventBase & {
      /**
       * A Wrath match's stalemate warning (instead of `waterWarning`): the calm-turn count reached
       * 19 at this TURN_END; if nobody loses HP in the turn it opens, the god comes at the end of
       * the turn after (`wrathStart` with `reason: 'stalemate'`).
       */
      readonly type: 'wrathStirs';
    })
  | (EventBase & {
      /**
       * The god appears, at a TURN_END: the timer's minute, a stalemate, or the 30-minute hard cap
       * (whichever came first). Its first `wrathMark` follows in the same tick.
       */
      readonly type: 'wrathStart';
      readonly reason: 'timer' | 'stalemate' | 'hardCap';
    })
  | (EventBase & {
      /**
       * A wave is marked, at a TURN_END (before that tick's `turnStart`): every living slime gets its
       * next turn, then the strike. `final`: the Final Judgment (no zones: the whole world).
       */
      readonly type: 'wrathMark';
      readonly wave: number;
      readonly zones: readonly WrathZone[];
      readonly final: boolean;
    })
  | (EventBase & {
      /**
       * The marked wave strikes at `impactTick` (the wind-up in between: the hand descends). At
       * `impactTick` the zones' `explosion` events (`source: 'wrath'`) follow — or, when `final`,
       * every living slime's `death` (`cause: 'judgment'`) and the `matchEnd` (a draw).
       */
      readonly type: 'wrathStrike';
      readonly wave: number;
      readonly impactTick: number;
      readonly final: boolean;
    })
  | (EventBase & {
      readonly type: 'matchEnd';
      /**
       * null = draw. A team match (W044, TEAMS.md §5.1): the lowest living slot of the winning
       * side (still null only for a draw).
       */
      readonly winner: number | null;
      /**
       * Team matches only (absent in a free-for-all, W044): the winning side's team (1 … 4), `0`
       * when a Solo seat won, null for a draw.
       */
      readonly winnerTeam?: SeatTeam | null;
      readonly placements: readonly WarsPlacement[];
    });

export type WarsEventType = WarsEvent['type'];

/**
 * The generic status-effect events (ADR-038, EFFECTS.md §2), alongside the effects' own events
 * in `WarsEvent` (`poisoned`, `armor`, `pickup` …), which stay as they are. W020a keeps them off
 * the `step()` stream — the wire contract the client checks type by type (`pickEvent`, the sound
 * and scene tables) — and hands them out through `WarsMatch.effectEvents()`; replication and
 * presentation are W020b's.
 */
export type WarsEffectEvent =
  | (EventBase & {
      /** An effect was applied or re-applied. `by`: the slot that caused it, or null. */
      readonly type: 'effectApplied';
      readonly slot: number;
      readonly effect: EffectId;
      readonly by: number | null;
      readonly stacks: number;
    })
  | (EventBase & {
      /** An effect ended (expired, consumed, cured, replaced, or with its slime). */
      readonly type: 'effectEnded';
      readonly slot: number;
      readonly effect: EffectId;
      readonly reason: EffectEndReason;
    });

export interface WarsPlacement {
  readonly slot: number;
  /**
   * 1 = winner; slimes eliminated in the same DEATHS step share a place. In a draw (no survivor)
   * every slime eliminated in the final turn shares 1st place.
   */
  readonly place: number;
  readonly damageDealt: number;
  readonly kills: number;
  readonly shots: number;
  readonly crates: number;
  readonly turnsSurvived: number;
  /** HP lost to its own attacks (W029b). */
  readonly selfDamage: number;
  /** Longest damaging shot: px, horizontal, fire position → another slime it damaged (W029b). */
  readonly longestShot: number;
  /**
   * Team matches only (absent in a free-for-all; W044, TEAMS.md §5.2–§5.3): the slime's side's
   * team (0 = Solo), the HP it removed from teammates and the teammates it took out (friendly fire
   * On). In a team match `place` is its side's place: places rank sides.
   */
  readonly team?: SeatTeam;
  readonly teamDamage?: number;
  readonly teamKills?: number;
}

/** One public status effect of a slime (EFFECTS.md §4). */
export interface SlimeEffectView {
  readonly id: EffectId;
  readonly stacks: number;
  /** Own turns left, or null (consumed / until the first turn / pending). */
  readonly turnsLeft: number | null;
  /**
   * W024a (W22R-o1): present when the effect's stat modifiers are not in force yet
   * (`EffectDef.inForce`: a Floaty picked up this turn, armed at its owner's next TURN_INTRO).
   * Absent: in force. Not on the wire (the protocol's effect string has no field for it), so a
   * decoded network view never has it.
   */
  readonly pending?: true;
}

export interface SlimeView {
  readonly slot: number;
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
  readonly facing: Facing;
  readonly elevation: number;
  readonly hp: number;
  readonly maxHp: number;
  readonly alive: boolean;
  readonly motion: SlimeMotionState;
  readonly weapon: WeaponId;
  readonly charging: boolean;
  /** Poison turns left (derived from `status.poison`). */
  readonly poisonTurns: number;
  /** Armor left (derived from `buff.armor`). */
  readonly armor: number;
  /** Active utilities in `BUFF_IDS` order (derived; `effects` will replace it, W020b). */
  readonly buffs: readonly BuffId[];
  /**
   * Public status effects (ADR-038, EFFECTS.md §4): every effect with a badge and every debuff,
   * in registry order. Not replicated yet (W020b).
   */
  readonly effects: readonly SlimeEffectView[];
  /** 0 … 1, 0 when not jetpacking. */
  readonly jetFuel: number;
  /**
   * First-turn guard (W016, W14R-M1): true until this slime's first turn starts — it cannot be
   * knocked back and cannot drop below 1 HP meanwhile, so everyone gets a turn (derived from
   * `status.guard`).
   */
  readonly guarded: boolean;
  /**
   * W054 (ADR-056, MOLEMAN.md §3): the slime's form — `'drill'` while the Moleman is its Drill
   * (from its `drillStart` tick through its `drillRevert`'s `endTick`), ABSENT for a slime (read
   * it as `view.form ?? 'slime'`), so every other slime's view is byte for byte what it was. The
   * drill points along the aim (`elevation` relative to `facing`, as a shot), its `motion` is
   * `walk` while it drives, `idle` while it holds still and `air` while it falls; its
   * collision circle is the slime's.
   */
  readonly form?: Exclude<SlimeForm, 'slime'>;
  /**
   * W055 (ADR-057, RULES §15): the dunce cap — `true` from the `tauntResult` that failed this
   * slime's taunt until one of its taunts lands (several flops: still one cap). ABSENT otherwise,
   * and on a dead slime's view (a grave wears no cap), so every view without a flop is byte for
   * byte what it was. Cosmetic: nothing in the rules reads it.
   */
  readonly dunce?: true;
}

export interface BodyView {
  readonly id: number;
  readonly kind: BodyKind;
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
  /** Radians, presentation. */
  readonly rot: number;
  /** Seconds left on a visible fuse / beep, 0 when none. */
  readonly fuse: number;
  readonly armed: boolean;
  readonly beeping: boolean;
  readonly parachute: boolean;
  /** Slot of the shooter, null for map objects and crates. */
  readonly owner: number | null;
  /**
   * W043 (LOOT.md §5, §11.1): a crate's tier — public, like its kind; ABSENT for Common (and every
   * non-crate), so a Classic view is byte for byte today's.
   */
  readonly tier?: UncommonTier;
}

export interface TurnView {
  /** null before the first turn and after the match. */
  readonly slot: number | null;
  readonly phase: TurnPhase;
  /** Tick at which the current phase ends by timer; null when it ends by condition. */
  readonly phaseEndsTick: number | null;
  readonly turn: number;
  readonly round: number;
  /** The active slot may still fire this turn (false during RETREAT, after the last shotgun shot …). */
  readonly canFire: boolean;
  /** The current target marker of the active slot, if any. */
  readonly target: { readonly x: number; readonly y: number } | null;
  /**
   * The movement budget (W019a, RULES §2), px: the active slime's movement points this turn
   * (the lobby's `movement` setting, × the Zoomies walk factor while it is active; the setting's
   * base when nobody is active). null = no limit (`movement: 'unlimited'`).
   */
  readonly moveBudget: number | null;
  /**
   * Movement points the active slime has left this turn (0 … `moveBudget`; spent only in its
   * AIMING and RETREAT, W037; reset at every TURN_INTRO). null = no limit.
   */
  readonly moveLeft: number | null;
}

/** One marked Wrath of God zone (ADR-046): its strike is a blast of radius `r` at (x, y). */
export interface WrathZone {
  /** Unique within the match (stable across waves' views, reconnects). */
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

/** The Wrath of God's public state (ADR-046); null in the view until the god appears. */
export interface WrathView {
  /** The marked wave (1 …). */
  readonly wave: number;
  /** The marked wave's zones (empty when `final`). */
  readonly zones: readonly WrathZone[];
  /** The marked wave is the Final Judgment. */
  readonly final: boolean;
  /** Strikes until the Final Judgment's, this wave's included (1 when `final`). */
  readonly judgmentInRounds: number;
  /** Living slots that still get a turn before the marked wave strikes (ascending). */
  readonly owed: readonly number[];
  /** The strike's impact tick during its wind-up; null otherwise. */
  readonly impactTick: number | null;
}

/**
 * One side of a team match (W044, TEAMS.md §2, §4.1): a team with its slots, or a Solo seat (team
 * 0, one slot). `last`: the slot that started the side's most recent turn (its cursor; null before
 * its first turn). Sides are listed in side order: teams A–D, then the Solo seats by slot.
 */
export interface SideView {
  readonly team: SeatTeam;
  /** Ascending; every slot of the side, living or not. */
  readonly slots: readonly number[];
  readonly last: number | null;
}

/** The public teams of a team match (W044, TEAMS.md §8.1); absent from a free-for-all's view. */
export interface TeamsView {
  /** Friendly fire (TEAMS.md §6): false = a teammate's attacks pass over you. */
  readonly friendlyFire: boolean;
  readonly sides: readonly SideView[];
}

export interface WarsMatchView {
  readonly tick: number;
  readonly width: number;
  readonly height: number;
  readonly theme: MapTheme;
  readonly style: MapStyle;
  /** −1 … 1 (× WIND_ACCEL in RULES §1). */
  readonly wind: number;
  readonly waterLevel: number;
  readonly suddenDeath: boolean;
  /** The Wrath of God (ADR-046): null unless it is the match's sudden death and has begun. */
  readonly wrath: WrathView | null;
  /**
   * W044 (ADR-051): the sides of a team match. ABSENT (not null) in a free-for-all, so a
   * free-for-all's view — and the golden view JSON — is exactly what it was.
   */
  readonly teams?: TeamsView;
  readonly terrainVersion: number;
  readonly turn: TurnView;
  readonly slimes: readonly SlimeView[];
  readonly bodies: readonly BodyView[];
  /** True once `matchEnd` was emitted. */
  readonly over: boolean;
}

/** Owner-only: weapon → charges (−1 = infinite, basic weapon). */
export type WarsInventory = Readonly<Partial<Record<WeaponId, number>>>;

/** A running match (TECH §6). `createWarsMatch(config)` builds one. */
export interface WarsMatch {
  readonly config: WarsMatchConfig;
  /** Read-only for callers: only the match carves it. */
  readonly terrain: TerrainMask;
  /** Latest held keys of a slot (ignored unless the slot may act). */
  setHeld(slot: number, keys: HeldKeys): void;
  /** Queue a one-shot command for the next `step()`; invalid ones are dropped and counted. */
  command(slot: number, cmd: WarsCommand): void;
  /** Advance one tick; returns this tick's events in creation order. */
  step(): readonly WarsEvent[];
  /** The status-effect events of the last `step()` (creation order; ADR-038, not on the wire yet). */
  effectEvents(): readonly WarsEffectEvent[];
  view(): WarsMatchView;
  /** Owner-only. */
  inventory(slot: number): WarsInventory;
  /** The slot's player is gone for good: eliminated at the start of its next turn (RULES §3). */
  abandon(slot: number): void;
  /** Commands dropped as invalid / not allowed so far (diagnostics). */
  readonly droppedCommands: number;
}
